/* =============================================================
   COR SHOWCASE — scroll-spy + helmet wrapper choreography
   Builds on cor-helmet-3d.js (auto-inits any [data-cor-helmet]).
   Drives:
     - .cor-showcase__rail-item.is-active (scroll spy)
     - .cor-showcase__chapter.is-active   (active chapter dimming)
     - stage data-light attribute          (halo swap)
     - --cs-helmet-yaw / --cs-helmet-scale (per-chapter transform)
     - HUD progress + frame readout
   Honors prefers-reduced-motion (no transforms, static frame).
   ============================================================= */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(n, lo, hi) { return Math.min(Math.max(n, lo), hi); }

  function pad2(n) { var s = String(n); return s.length < 2 ? '0' + s : s; }

  function initShowcase(root) {
    if (!root || root.__corShowcaseBound) return;
    root.__corShowcaseBound = true;

    var stage     = root.querySelector('[data-cor-stage]');
    var helmetWrap = root.querySelector('[data-cor-helmet-wrap]');
    var progress  = root.querySelector('[data-cor-progress]');
    var readout   = root.querySelector('[data-cor-readout-frame]');
    var chapters  = Array.prototype.slice.call(root.querySelectorAll('[data-cor-chapter]'));
    var railItems = Array.prototype.slice.call(root.querySelectorAll('[data-cor-rail-item]'));

    if (!stage || chapters.length === 0) return;

    var total = chapters.length;
    var totalPadded = pad2(total);

    var active = -1;

    function applyChapter(idx, smooth) {
      if (idx === active) return;
      active = idx;

      var ch = chapters[idx];
      if (!ch) return;

      var light = ch.getAttribute('data-cor-light') || 'key';
      var yaw   = parseFloat(ch.getAttribute('data-cor-rotation') || '0');

      // light rig swap
      stage.setAttribute('data-light', light);

      // chapters dim/lift
      chapters.forEach(function (c, i) {
        c.classList.toggle('is-active', i === idx);
      });

      // rail scroll spy
      railItems.forEach(function (a, i) {
        a.classList.toggle('is-active', i === idx);
      });

      // HUD progress
      if (progress) {
        progress.textContent = pad2(idx + 1) + ' / ' + totalPadded;
      }

      // frame readout (presentation only — not actual three.js yaw)
      if (readout) {
        var sign = yaw >= 0 ? '+' : '−';
        var abs = Math.abs(yaw).toFixed(1);
        readout.textContent = sign + abs + '°';
      }

      // helmet wrapper transform (CSS-driven; bundled three scene is unaware
      // and continues running. Wrapper rotateY + slight scale per chapter.)
      if (!REDUCE && helmetWrap) {
        var scale = 1 + Math.min(Math.abs(yaw) / 240, 0.06);
        var ypct = idx === 0 ? 0 : (idx % 2 === 0 ? -2 : 2);
        helmetWrap.style.setProperty('--cs-helmet-yaw', yaw + 'deg');
        helmetWrap.style.setProperty('--cs-helmet-scale', scale.toFixed(3));
        helmetWrap.style.setProperty('--cs-helmet-y', ypct + '%');
      }

      void smooth;
    }

    // Initial state — chapter 0 lit
    applyChapter(0, false);

    // Scroll-spy via IntersectionObserver — choose the chapter whose top
    // is nearest to the viewport vertical midpoint.
    var supportsIO = 'IntersectionObserver' in window;

    if (supportsIO) {
      var io = new IntersectionObserver(function (entries) {
        // Pick the entry with highest intersectionRatio whose target is visible
        var best = null;
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        });
        if (best) {
          var idx = chapters.indexOf(best.target);
          if (idx >= 0) applyChapter(idx, true);
        }
      }, {
        rootMargin: '-35% 0px -45% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1]
      });

      chapters.forEach(function (c) { io.observe(c); });
    } else {
      // Fallback: scroll listener
      var onScroll = function () {
        var mid = window.scrollY + window.innerHeight * 0.4;
        var bestIdx = 0;
        var bestDist = Infinity;
        for (var i = 0; i < chapters.length; i++) {
          var r = chapters[i].getBoundingClientRect();
          var top = r.top + window.scrollY;
          var d = Math.abs(top - mid);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        }
        applyChapter(bestIdx, true);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    // Rail click — smooth-scroll to chapter
    railItems.forEach(function (a, i) {
      a.addEventListener('click', function (ev) {
        var target = chapters[i];
        if (!target) return;
        ev.preventDefault();
        var top = target.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.25;
        window.scrollTo({ top: top, behavior: REDUCE ? 'auto' : 'smooth' });
      });
    });

    // BILINGUAL — flip data-cor-* text per current document language.
    // The site exposes language via [dir] or html.lang or .cor-lang-he class.
    function applyLang() {
      var docDir = document.documentElement.getAttribute('dir') || 'rtl';
      var docLang = (document.documentElement.lang || 'he').toLowerCase();
      var useEn = docDir === 'ltr' || docLang.indexOf('en') === 0 || document.documentElement.classList.contains('cor-lang-en');
      var attr = useEn ? 'data-cor-en' : 'data-cor-he';
      root.querySelectorAll('[data-cor-en][data-cor-he]').forEach(function (el) {
        var v = el.getAttribute(attr);
        if (v != null && v.length) el.textContent = v;
      });
    }
    applyLang();
    // Re-apply on language toggle events (cor-landing-fx emits this)
    document.addEventListener('cor:lang', applyLang);

    // If reduced motion, ensure helmet auto-spin from cor-helmet-3d.js
    // is already off (we set data-cor-spinning="false" in Liquid).
    void REDUCE;
  }

  function bootAll() {
    document.querySelectorAll('[data-cor-chapters]').forEach(function (root) {
      initShowcase(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    var sec = e.target && e.target.querySelector ? e.target.querySelector('[data-cor-chapters]') : null;
    if (sec) initShowcase(sec);
  });
})();
